import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Check } from "lucide-react";
import stats from "./content/site-stats.json";
import { useMeta } from "../hooks/useMeta";
import { Shell, H2 } from "./parts";
import { DRAFT_BASE } from "./DraftLayout";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", email: "", company: "", website: "", message: "" };

function Field({ id, label, type = "text", required, value, onChange, textarea }) {
  const cls = "mt-2 w-full rounded-lg border border-mist bg-white px-4 py-3 text-base text-navy outline-none transition-colors focus:border-navy";
  return (
    <label htmlFor={id} className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-navy/70">{label}{required && " *"}</span>
      {textarea ? (
        <textarea id={id} data-testid={`contact-${id}`} required={required} rows={5} value={value} onChange={onChange} className={cls} />
      ) : (
        <input id={id} data-testid={`contact-${id}`} type={type} required={required} value={value} onChange={onChange} className={cls} />
      )}
    </label>
  );
}

export default function Contact() {
  useMeta({ title: "Contact | Smart Marketer Agency", description: "Tell us about your brand. We reply within two business days." });
  const { pathname } = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, ref: pathname.replace(DRAFT_BASE, "") || "/" }),
      });
      if (!res.ok) throw new Error("failed");
      setState("sent");
      setForm(EMPTY);
    } catch {
      setState("error");
    }
  };

  return (
    <main data-testid="draft-contact">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-20 md:py-28">
          <h1 className="text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Let's talk.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">Tell us about your brand and what you want to fix. We reply within two business days.</p>
        </div>
      </section>

      <Shell testId="contact-body">
        <div className="grid gap-12 md:grid-cols-[1fr_1.4fr]">
          <div>
            <H2>Reach us directly.</H2>
            <a href={`mailto:${stats.email}`} data-testid="contact-email-link" className="mt-6 inline-flex items-center gap-3 text-lg font-bold text-link hover:underline">
              <Mail size={22} /> {stats.email}
            </a>
            <p className="mt-6 text-base leading-relaxed text-navy/70">Want to skip ahead? The fit check takes about five minutes and tells us what we need to know before a call.</p>
            <Link to={`${DRAFT_BASE}/apply?ref=${encodeURIComponent("/contact")}`} data-testid="contact-apply-link" className="mt-4 inline-block rounded-full border-2 border-navy px-6 py-3 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white">Check the fit</Link>
          </div>

          {state === "sent" ? (
            <div data-testid="contact-success" className="flex flex-col items-start justify-center rounded-2xl bg-fog p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green text-white"><Check size={26} strokeWidth={3} /></span>
              <h3 className="mt-6 text-2xl font-bold text-navy">Got it. Thank you.</h3>
              <p className="mt-3 text-base text-navy/70">We'll reply within two business days.</p>
              <button data-testid="contact-send-another" onClick={() => setState("idle")} className="mt-6 text-sm font-bold text-link hover:underline">Send another message</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-mist bg-fog p-8 md:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="name" label="Name" required value={form.name} onChange={set("name")} />
                <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} />
                <Field id="company" label="Brand" value={form.company} onChange={set("company")} />
                <Field id="website" label="Website" value={form.website} onChange={set("website")} />
              </div>
              <div className="mt-5">
                <Field id="message" label="What do you want to fix?" required textarea value={form.message} onChange={set("message")} />
              </div>
              {state === "error" && <p data-testid="contact-error" className="mt-4 text-sm font-bold text-pink">Something went wrong. Email us at {stats.email} instead.</p>}
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={state === "sending"}
                className="mt-6 rounded-full bg-pink px-8 py-4 text-base font-bold text-white transition-colors hover:bg-pink-dark disabled:opacity-60"
              >
                {state === "sending" ? "Sending..." : "Send message"}
              </button>
            </form>
          )}
        </div>
      </Shell>
    </main>
  );
}
